<template>
    <DCard
        padding
    >
        <DRow>
            <DCol sm="4">
                <h6>
                    {{ __('messages.search') }}
                </h6>
            </DCol>
            <DCol sm="4">
                <h6>
                    {{ __('products.fulfillmentAccounts.filter.fulfillment_groups') }}
                </h6>
            </DCol>
        </DRow>
        <DRow>
            <DCol sm="4">
                <DInput
                    id="search"
                    v-model="filter.search"
                    :placeholder="__('products.fulfillmentAccounts.filter.search')"
                />
            </DCol>
            <DCol sm="4">
                <DMultiSelect
                    id="fulfillment-groups-dropdown"
                    :options="fulfillmentGroupsOptions"
                    :search="true"
                    :selection="true"
                    :selected="selectedFulfillmentGroups"
                    :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                    selection-type="text"
                    @update="updateFulfillmentGroups"
                />
            </DCol>
            <DCol sm="4">
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="submitFilters"
                />
                <DButton
                    id="filter-reset"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <section class="accounts-table">
            <header>
                <div>{{ __('products.fulfillmentAccounts.index.table.headers.name') }}</div>
                <div>{{ __('products.fulfillmentAccounts.index.table.headers.status') }}</div>
                <div>{{ __('products.fulfillmentAccounts.index.table.headers.num_instances') }}</div>
                <div>{{ __('products.fulfillmentAccounts.index.table.headers.max_instances') }}</div>
                <div>{{ __('messages.actions.actions') }}</div>
            </header>
            <main>
                <div
                    v-for="(item, index) in fulfillmentAccountGroups"
                    :key="index"
                    class="item-group"
                >
                    <div class="item-header">
                        <div>
                            <DButton
                                :id="`toggle_group_${item.id}`"
                                mini
                                :icon="collapsedGroups.includes(item.id) ? 'arrow-up' : 'arrow-down'"
                                @click="toggleGroup(item.id)"
                            />
                        </div>
                        <div>{{ item.name }} / {{ item.module }}</div>
                        <div style="display: flex; grid-gap: 10px; justify-content: flex-start;">
                            <DButton
                                v-if="userPermissions.includes('fulfillment.edit') && item.module === 'antagonist-da'"
                                :id="`bulk_change_passwords_${item.id}`"
                                icon="shield-edit"
                                mini
                                @click="changeAntagonistPasswords(item)"
                            />
                            <DButton
                                v-if="userPermissions.includes('fulfillment.edit')"
                                :id="`edit_group_${item.id}`"
                                icon="pencil"
                                mini
                                :href="editFulfillmentAccountGroup(item.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('fulfillment.delete') && item.accounts.length === 0"
                                :id="`edit_group_${item.id}`"
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="deleteAccount(item.id)"
                            />
                        </div>
                    </div>
                    <div
                        v-for="(account, indexAccount) in item.accounts"
                        v-show="!collapsedGroups.includes(item.id)"
                        :key="indexAccount"
                        class="item"
                    >
                        <div />
                        <div>{{ account.name }}</div>
                        <div>{{ account.status }}</div>
                        <div>{{ account.num_instances }}</div>
                        <div>{{ account.max_instances }}</div>
                        <div style="display: flex; grid-gap: 10px; justify-content: flex-start;">
                            <DButton
                                v-if="userPermissions.includes('fulfillment.view') || userPermissions.includes('fulfillment.edit')"
                                :id="`view_account_${account.id}`"
                                icon="eye"
                                mini
                                :href="viewFulfillmentAccount(account.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('fulfillment.edit')"
                                :id="`edit_account_${account.id}`"
                                icon="pencil"
                                mini
                                :href="editFulfillmentAccount(account.id)"
                            />
                            <DButton
                                v-if="userPermissions.includes('fulfillment.delete') && account.contracts_count === 0"
                                :id="`delete_account_${account.id}`"
                                icon="trash-can-outline"
                                color="danger"
                                mini
                                @click="deleteAccount(account.id, 'account')"
                            />
                            <DButton
                                v-if="item.adminSsoSupported"
                                :id="`sso_account_${account.id}`"
                                :label="__('products.fulfillmentAccounts.index.singleSignOn')"
                                mini
                                @click="adminLoginViaSSO(account.id)"
                            />
                        </div>
                    </div>
                </div>
            </main>
        </section>

        <DFabs>
            <DFab
                v-if="userPermissions.includes('fulfillment.create')"
                id="btn-add"
                icon="plus"
                :label="__('products.fulfillmentAccounts.index.addFulfillmentAccountButton')"
                @click="$inertia.get($route('products.accounts.create'))"
            />
            <DFab
                v-if="userPermissions.includes('fulfillment.create')"
                id="btn-add-fulfillment-group"
                icon="plus"
                color="secondary"
                :label="__('products.fulfillmentAccounts.index.addFulfillmentGroupButton')"
                @click="$inertia.get($route('products.account-groups.create'))"
            />
        </DFabs>

        <DModal
            v-model:show="showConfirmationModal"
            :title="__('products.fulfillmentAccounts.index.delete_confirmation.title')"
            :message="__('products.fulfillmentAccounts.index.delete_confirmation.message')"
            is-confirm
            is-error
            @close="showConfirmationModal = false"
            @confirm="deleteCallBack"
        />

        <ChangeAntagonistPasswordsModal
            :account-group="changeAntagonistPasswordsAccountGroup"
            :show-modal="showChangeAntagonistPasswordsModal"
            @close-modal="closeChangeAntagonistPasswordsModal"
        />
    </DCard>
</template>

<script>

import ChangeAntagonistPasswordsModal from '~@/Components/Products/FulfillmentAccountGroups/ChangeAntagonistPasswordsModal.vue'

export default {
    name: 'FulfillmentAccountIndex',
    components: {
        ChangeAntagonistPasswordsModal
    },
    props: {
        fulfillmentAccountGroups: {
            type: Array,
            required: true
        },
        allFulfillmentAccountGroups: {
            type: Array,
            required: true
        },
        filterData: {
            type: Object,
            default () {
                return {}
            }
        }
    },
    data () {
        return {
            changeAntagonistPasswordsAccountGroup: null,
            collapsedGroups: [],
            filter: {
                search: (this.filterData !== null && this.filterData.search) || this.getParam('search'),
                fulfillmentGroups: (this.filterData !== null && this.filterData.fulfillmentGroups) || this.getParam('fulfillmentGroups')
            },
            ssoLink: null,
            showConfirmationModal: false,
            showChangeAntagonistPasswordsModal: false,
            deleteCallBack: () => { return {} }
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        fulfillmentGroupsOptions () {
            return this.allFulfillmentAccountGroups.map((item) => {
                return { value: item.value, text: item.label, selected: this.filterData?.fulfillmentGroups?.includes(String(item.value)) }
            })
        },
        selectedFulfillmentGroups () {
            if (!this.filter.fulfillmentGroups) return []

            return this.filter.fulfillmentGroups.map((item) => parseInt(item))
        }
    },
    methods: {
        changeAntagonistPasswords (accountGroup) {
            this.changeAntagonistPasswordsAccountGroup = accountGroup
            this.showChangeAntagonistPasswordsModal = true
        },
        closeChangeAntagonistPasswordsModal () {
            this.showChangeAntagonistPasswordsModal = false
            this.changeAntagonistPasswordsAccountGroup = null
        },
        editFulfillmentAccountGroup (id) {
            return this.$route('products.account-groups.edit', { account_group: id })
        },
        editFulfillmentAccount (id) {
            return this.$route('products.accounts.edit', { account: id })
        },
        viewFulfillmentAccount (id) {
            return this.$route('products.accounts.show', { account: id })
        },
        toggleGroup (groupId) {
            if (this.collapsedGroups.includes(groupId)) {
                this.collapsedGroups = this.collapsedGroups.filter(e => e !== groupId)
            } else {
                this.collapsedGroups.push(groupId)
            }
        },
        async generateAdminSSOLink (id) {
            const self = this
            try {
                const response = await window.axios.get(this.$route('products.accounts.sso', id))
                self.ssoLink = response.data.url
            } catch (e) {
                self.ssoLink = null
            }
        },
        login () {
            window.open(this.ssoLink, '_blank').focus()
        },
        async adminLoginViaSSO (id) {
            await this.generateAdminSSOLink(id)

            if (this.ssoLink === null) {
                return this.$inertia.get(this.$route('products.accounts.index'), {}, {
                    preserveScroll: true
                })
            }

            this.login()
        },
        updateFulfillmentGroups (values) {
            this.filter.fulfillmentGroups = values
        },
        submitFilters () {
            this.$inertia.get(this.$route('products.accounts.index', {
                _query: this.filter,
                saveFilters: true
            }))
        },
        resetFilters () {
            this.$inertia.get(this.$route('products.accounts.index', {
                saveFilters: true,
                defaultFilters: true
            }))
        },
        getParam (paramName) {
            const params = new URLSearchParams(window.location.search)
            return params.has(paramName) ? params.get(paramName) : null
        },
        deleteAccount (id, value = null) {
            this.deleteCallBack = () => {
                const routeName = value === 'account' ? 'products.accounts.destroy' : 'products.account-groups.destroy'
                this.$inertia.delete(this.$route(routeName, id))
                this.showConfirmationModal = false
            }
            this.showConfirmationModal = true
        }
    }
}
</script>

<style scoped>
.accounts-table {
}
.accounts-table header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    display: grid;
    grid-template-columns: 45px 300px 175px 175px 1fr minmax(230px, auto);
    grid-gap: 10px;
}
.accounts-table header > div:nth-child(1) {
    grid-column: 2;
}
.accounts-table header > div {
    padding: 10px;
    font-weight: bold;
}
.accounts-table main {
}
.accounts-table main .item-group {
    border-bottom: 1px solid rgba(0,0,0,0.3);
    position: relative;
}
.accounts-table main .item-group .item-header {
    display: grid;
    grid-template-columns: 45px 300px 175px 175px 1fr minmax(230px, auto);
    grid-gap: 10px;
    background: #fff;
    border-bottom: 1px solid rgba(0,0,0,0.14);
    position: sticky;
    top: -25px;
    width: 100%;
    z-index: 100;
}
.accounts-table main .item-group .item-header > div {
    padding: 10px;
    align-self: center;
}
.accounts-table main .item-group .item-header > div:nth-child(2) {
    grid-column: 2 / span 4;
    font-weight: bold;
}
.accounts-table main .item-group .item {
    display: grid;
    grid-template-columns: 45px 300px 175px 175px 1fr minmax(230px, auto);
    grid-gap: 10px;
}
.accounts-table main .item-group .item > div {
    padding: 10px;
    align-self: center;
}
.accounts-table main .item-group .item > div:nth-child(1) {
    position: relative;
    overflow: hidden;
    padding: 0;
    height: 100%;
}
.accounts-table main .item-group .item > div:nth-child(1)::after {
    content: "";
    display: block;
    position: absolute;
    left: 21px;
    top: 0;
    width: 2px;
    height: 60px;
    background: rgba(0,0,0,0.07);
}
.accounts-table main .item-group .item > div:nth-child(1)::before {
    content: "";
    display: block;
    position: absolute;
    left: 23px;
    top: 25px;
    width: 10px;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
    height: 2px;
    background: rgba(0,0,0,0.07);
}
.accounts-table main .item-group .item:last-of-type > div:nth-child(1)::after {
    height: 35px;
    background: rgba(0,0,0,0.07);
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
}
.accounts-table main .item-group .item:hover {
    background: rgba(0,0,0,0.07);
}
</style>
